import React from 'react';
import { cn } from '@crisellevaldez/speculo-ui';
import { Zap } from 'lucide-react';

interface RemiCardHeaderProps {
  /** Uppercase, tracked-wide label (e.g. "Suggested Contact Status"). */
  eyebrow: string;
  /** Optional title row under the eyebrow (e.g. "Remi recommends: Handoff"). */
  title?: React.ReactNode;
  /** Optional right-aligned slot — used for confidence scores, actions, etc. */
  rightSlot?: React.ReactNode;
  /** Override the default Sparkles glyph. */
  icon?: React.ReactNode;
  className?: string;
}

/**
 * Shared header for any Remi-authored card surface.
 *
 * Locks the visual identity for "Remi made this content" in one place:
 * - the amber→orange gradient glyph (`.summarize-pill` — same as the
 *   AskRemi "Summarize" button), and
 * - the small uppercase eyebrow label.
 *
 * Used by SuggestedStatusCard and intended for any future Remi cards
 * (current status summary, what Remi knows, outreach plan, etc.) so they
 * share one consistent look.
 */
export const RemiCardHeader: React.FC<RemiCardHeaderProps> = ({
  eyebrow,
  title,
  rightSlot,
  icon,
  className,
}) => (
  <div className={cn('flex flex-col sm:flex-row sm:items-start gap-3', className)}>
    <div className="flex items-center gap-2.5 lg:gap-3 flex-1 min-w-0">
      {/* Same look as the AskRemi header + dashboard glyph — Zap icon in
          a rounded-md `btn-ai-gradient` square, white icon on the
          amber→orange gradient. */}
      <div className="btn-ai-gradient rounded-md p-1.5 lg:p-1.5 2xl:p-2 shrink-0 flex items-center justify-center">
        {icon ?? <Zap className="h-3.5 w-3.5 lg:h-3.5 lg:w-3.5 2xl:h-4 2xl:w-4 text-white dark:text-zinc-900" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] lg:text-[10px] 2xl:text-[11px] font-semibold tracking-wider text-muted-foreground uppercase leading-tight">
          {eyebrow}
        </div>
        {title && (
          <div className="text-xs lg:text-xs xl:text-xs 2xl:text-sm font-medium text-foreground mt-0.5 leading-tight">
            {title}
          </div>
        )}
      </div>
    </div>
    {rightSlot}
  </div>
);
